import React, { useState, useEffect } from 'react';
import './search-menu.css';
import Role from '../../Components/role-layer/Role';
import CookiesServices from '../../services/CookiesService';
import SearchPopup from './search-popup/SearchPopup';
import SearchBar from '../../Components/search-bar/SearchBar';
import DatePicker from '../../Components/date-picker/DatePicker';
import PrimaryButton from '../../Components/primary-button/PrimaryButton';
import Feedback from '../../Components/feedback/Feedback';
import { MdCancel } from 'react-icons/md';
import { FaUser, FaUsers, FaBuilding } from 'react-icons/fa';
import { CircularProgress } from '@mui/material';

const SearchMenu = ({ onClose }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [roles, setRoles] = useState([]);
  const [subroles, setSubroles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [searchValueForRole, setSearchValueForRole] = useState('');
  const [searchValueForSubrole, setSearchValueForSubrole] = useState('');
  const [roleType, setRoleType] = useState('');
  const [popupTitle, setPopupTitle] = useState('');
  const [selectedCategoryCode, setSelectedCategoryCode] = useState(null);
  const [resetDates, setResetDates] = useState(false);
  const cookiesServices = new CookiesServices();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [name, setName] = useState('');
  const [tempKpiMode, setTempKpiMode] = useState('');
  const [tempKpiUser, setTempKpiUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [designationName, setDesignationName] = useState('');
  const [designationCode, setDesignationCode] = useState(cookiesServices.get('designationCode') || null);
  const [peopleName, setPeopleName] = useState('');
  const [peopleCode, setPeopleCode] = useState('');
  const [popupError, setPopupError] = useState('');  
  const [agentSelected, setAgentSelected] = useState(false);
  const [locationCodes, setLocationCodes] = useState([]);
  const [isKpiReady, setIsKpiReady] = useState(false);
  const [isSpecialRootSelected, setIsSpecialRootSelected] = useState(false);
  const [isPopupSelectionValid, setIsPopupSelectionValid] = useState(false);

  const headers = cookiesServices.getHeaders();

  useEffect(() => {
    setLoading(true);
    fetch(cookiesServices.toUrl('v1', '/user/hierarchy'), {
      method: 'GET',
      headers,
  })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.levels)) setLevels(data.levels);
        if (Array.isArray(data.hierarchy)) {
          const rolesMapped = data.hierarchy.map(h => ({
            id: h.code,
            order: h.order,
            name: h.shortName,
            designationName: h.designationName || '',
          }));
          setRoles(rolesMapped);

          if (Array.isArray(data.locations)) {
            const locCodes = data.locations.map(l => l.code);
            setLocationCodes(locCodes);
          }
          const restoredRole = cookiesServices.get('searchValueForRole');
          const restoredSubrole = cookiesServices.get('searchValueForSubrole');
          const restoredOrder = cookiesServices.get('selectedRoleOrder');

          if (restoredRole) setSearchValueForRole(restoredRole);
          if (restoredSubrole) setSearchValueForSubrole(restoredSubrole);

          if (restoredOrder) {
            const found = rolesMapped.find(r => r.order == restoredOrder);
            if (found) { 
              setSelectedRole(found);

              // Show subroles if designationCode exists
              if (designationCode) {
                const arr = rolesMapped
                  .filter(r => r.order > found.order && r.order <= 8)
                  .map(r => ({
                    order: r.order,
                    id: r.id,
                    name: r.designationName,
                    designationName: r.designationName,
                  }));
                setSubroles(arr);
                cookiesServices.set('subroles', JSON.stringify(arr));
              }
            }
          }
        }
      })
      .catch(err => {
        console.error('Error fetching user levels:', err);
        setFeedback({ type: 'error', message: 'Failed to load user levels.' });
      })
      .finally(()=> setLoading(false));
  }, []);

  useEffect(() => {
    if (resetDates) setTimeout(() => setResetDates(false), 0);
  }, [resetDates]);

  const handleRoleSelect = async (role) => {
    setSelectedRole(role);
    setIsKpiReady(false);
    setTempKpiMode('');
    setTempKpiUser('');

    setSelectedCategoryCode(null);
    setDesignationCode(null);
    setPeopleCode('');
    setPeopleName('');
    setSearchValueForSubrole('');

    setPopupError('');

    if (role.order === 0) {

      setIsSpecialRootSelected(true);

      const matchedLevel = levels.find(lvl => lvl.id === role.id);

      const specialRoleText = matchedLevel ? `${matchedLevel.code} - ${matchedLevel.shortName}` : role.designationName || '';

      setSearchValueForRole(specialRoleText);
      setSearchValueForSubrole('');

      const firstLevel = levels?.[0];

      setTempKpiMode(firstLevel?.id || '');
      setTempKpiUser(firstLevel?.code || '');

      setDesignationCode(firstLevel?.code || null);
      setSelectedCategoryCode(firstLevel?.code || '');
      setDesignationName(firstLevel?.shortName || '');
      setIsPopupSelectionValid(true);

      const arr = roles
        .filter(r => r.order > role.order && r.order <= 8)
        .map(r => ({
          order: r.order,
          id: r.id,
          name: r.designationName,
          designationName: r.designationName,
        }));

      setSubroles(arr);
      setIsPopupVisible(false);
      return;
    }
  setAgentSelected(false);
  setPopupError('');

  const isSameRole = selectedRole?.id === role.id;
  const hasValidData = categories?.length > 0;

  if (isSameRole && hasValidData) {
    setIsPopupVisible(true);
    setPopupTitle(`Select the ${role.name}`);
    setRoleType('role');
    return;
  }

  setSelectedRole(role);
  setPopupTitle(`Select the ${role.name}`);
  setIsPopupVisible(true);
  setSearchValueForSubrole('');
  setSubroles([]);
  setCategories([]);
  setRoleType('role');
 
  try {
    setLoading(true);
    const response = await
      fetch(cookiesServices.toUrl( 'v1', `/user/hierarchy_designations/${role.id}`), {
        method: 'GET',
        headers,
      })
      if (!response.ok) {
        if (response.status === 500) {
          // cookiesServices.clearLogin();
        }
        throw new Error('Failed to fetch designations');
      }

      const data = await response.json();
      const finalData = data.designations || data.categories || [];
      setCategories(finalData);
    } catch (err) {
      console.error('Error fetching designations:', err);
      setFeedback({ type: 'error', message: 'Error fetching designations. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubroleSelect = async (subrole) => {
    setAgentSelected(false);
    setPopupError('');

    const from = selectedRole?.id;
    const desCode = designationCode;
    const to = subrole.id;

    setPopupTitle(`Select the ${subrole.name}`);
    setIsPopupVisible(true);
    setRoleType('subRole');

    try {
      setLoading(true);
      const response = await

        fetch(cookiesServices.toUrl( 'v1', `/user/hierarchy_people/${from}/${desCode}/${to}`), {
          method: 'GET',
          headers,
        })

      if (!response.ok) {
        if (response.status === 500) {
          // cookiesServices.clearLogin();
        }
        throw new Error('Failed to fetch hierarchy people');
      }

      const data = await response.json();
      const finalData = data.designations || data.categories || [];
      setCategories(finalData);
    } catch (err) {
      console.error('Error fetching designations:', err);
      setFeedback({ type: 'error', message: 'Error fetching designations. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAgentSelect = (agent) => {
    setAgentSelected(true);
    setPopupError('');

    if (roleType === 'role') {
      setSearchValueForRole(`${agent.code} - ${agent.name}`);
      setTempKpiMode(selectedRole?.id || '');
      setTempKpiUser(agent.code || '');
      setDesignationCode(agent.code);
      setDesignationName(agent.name);
      // Load subroles immediately after selecting agent from role popup
      const arr = roles
        .filter(r => r.order > selectedRole.order && r.order <= 8)
        .map(r => ({
          order: r.order,
          id: r.id,
          name: r.designationName,
          designationName: r.designationName,
        }));
      setSubroles(arr);

      cookiesServices.set('subroles', JSON.stringify(arr));

    } else {
      setSearchValueForSubrole(`${agent.code} - ${agent.name}`);
      setTempKpiMode('TEAM');
      setTempKpiUser(agent.code || '');
      cookiesServices.set('name', agent.name);
      setPeopleCode(agent.code);
      setPeopleName(agent.name);
    }

    setSelectedCategoryCode(agent.code);
    setIsKpiReady(true);
    setIsPopupVisible(false);
    setIsPopupSelectionValid(true);
  };

  const handleFetch = (mode = null) => {
    if (fromDate && toDate) {
      cookiesServices.set('from_date', fromDate);
      cookiesServices.set('to_date', toDate);
    }

    if (selectedCategoryCode) {
      cookiesServices.set('searchByAgent', selectedCategoryCode);
    }

    if (selectedLevel?.shortName) {
      cookiesServices.set('Level', selectedLevel.shortName);
    }

    let finalKpiMode = '';
    let finalKpiUser = '';

    const firstRoleLevel = levels?.[0];

    if (firstRoleLevel && selectedRole?.order === 0) {
      finalKpiMode = firstRoleLevel.id;
      finalKpiUser = firstRoleLevel.code;
    } else {
      finalKpiMode = tempKpiMode;
      finalKpiUser = tempKpiUser;
    }

    if (mode) {
      finalKpiMode = mode;
    }

    if (mode === 'TEAM') {
      finalKpiUser = peopleCode || finalKpiUser;
    }

    if (mode === 'SELF') {
      finalKpiUser = selectedCategoryCode || finalKpiUser;
    }

    if (!finalKpiMode || !finalKpiUser) {
      setPopupError('Please complete selection before fetching.');
      return;
    }

    cookiesServices.set('kpiMode', finalKpiMode);
    cookiesServices.set('kpiUser', finalKpiUser);
    cookiesServices.set('searchValueForRole', searchValueForRole);
    cookiesServices.set('searchValueForSubrole', searchValueForSubrole);
    cookiesServices.set('designationCode', designationCode);
    cookiesServices.set('designationName', designationName);
    cookiesServices.set('peopleCode', peopleCode);
    cookiesServices.set('name',peopleName || designationName || '');cookiesServices.set('selectedRoleOrder', selectedRole?.order || '');
    cookiesServices.clearTempKpi();

    window.dispatchEvent(new Event('level_changed'));
    onClose();
  };

  const handleReset = () => {
    setSelectedRole(null);
    setSearchValueForRole('');
    setSearchValueForSubrole('');
    setSubroles([]);
    setCategories([]);
    setSelectedCategoryCode(null);
    setDesignationCode(null);
    setPeopleCode('');
    setPeopleName('');
    setTempKpiMode('');
    setTempKpiUser('');
    setPopupError('');
    setIsPopupVisible(false);

    cookiesServices.remove('kpiMode');
    cookiesServices.remove('kpiUser');
    cookiesServices.remove('searchValueForRole');
    cookiesServices.remove('searchValueForSubrole');
    cookiesServices.remove('designationCode');
    cookiesServices.remove('peopleCode');
    cookiesServices.remove('searchByAgent');
  };

  const handleLevelSelect = (lvl) => {
    setSelectedLevel(lvl);
    cookiesServices.set('kpiMode', lvl.id || '');
    cookiesServices.set('kpiUser', lvl.code || '');
    cookiesServices.set('level', lvl.level || '');
    cookiesServices.set('name', lvl.shortName);
    setName(lvl.shortName);
    window.dispatchEvent(new Event('level_changed'));
    onClose();
  };

  const getLevelIcon = (type) => {
    switch (type?.toUpperCase()) {
      case 'SELF': return <FaUser />;
      case 'TEAM': return <FaUsers />;
      default: return <FaBuilding />;
    }
  };

  const iconColors = ['color-red', 'color-blue', 'color-green'];

  // Handler for closing Feedback popup
  const handleFeedbackClose = () => {
    setFeedback({});
  };

  const isLocationSelected =
    selectedRole?.id &&
    locationCodes.includes(selectedRole.id) &&
    !searchValueForSubrole;

  const rawRole = cookiesServices.get('role');

  const roleList = (rawRole || '')
    .split('|')
    .map(r => r.trim().toUpperCase())
    .filter(Boolean);

  const isCEO = roleList.includes('CEO');

  const injectedRole = levels
  .filter(lvl => lvl.id !== 'TEAM' && lvl.id !== 'SELF')
  .slice(0, 1)[0];

  const isSpecialRole = selectedRole?.order === 0;

  const isAnySelectionMade = selectedRole || searchValueForRole || searchValueForSubrole || selectedLevel;

  return (
    <div className="search-menu-container">
      <div className="overlay" onClick={onClose} />
      <div className="search-menu">
        <div className="search-menu-header">
          Sort Options
          <div className="close-button">
            <MdCancel color="#E30613" size={24} onClick={onClose} />
          </div>
        </div>

        {!isCEO && (
          <div className="level-dropdown">
            {levels
              .filter(lvl => lvl.id !== injectedRole?.id)
              .slice(0, 3)
              .map((lvl, idx) => (
                <div
                className={`level-item ${selectedLevel?.id === lvl.id ? 'active-level' : ''}`}
                key={idx}
                onClick={() => handleLevelSelect(lvl)}
              >
                <div className={`level-item-icon ${iconColors[idx % iconColors.length]}`}>
                  {getLevelIcon(lvl.name)}
                </div>
                <div className="level-item-label">
                  <span>{lvl.name}</span>
                  <span>{lvl.shortName || lvl.code}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="content">
          <Role
            roles={[
              ...(injectedRole
                ? [{
                  id: injectedRole.id,
                  name: injectedRole.name,
                  designationName:injectedRole.shortName || injectedRole.name || injectedRole.id,
                  order: 0,
                }]
                : []),
              ...roles
            ]}
            title="Select by Role"
            onRoleSelect={handleRoleSelect}
            roleType="role"
            reset={resetDates}
          />
          {searchValueForRole && <SearchBar searchValue={searchValueForRole} />}

          {subroles.length > 0 && (
            <Role
              roles={subroles}
              title="Select by SubRole"
              onRoleSelect={handleSubroleSelect}
              roleType="subRole"
              reset={resetDates}
              showDesignationName={true}
            />
          )}
          {searchValueForSubrole && <SearchBar searchValue={searchValueForSubrole} />}

          <div className="search-buttons-inline-container">

            {(isSpecialRole && !searchValueForSubrole) || isLocationSelected ? (
              <PrimaryButton
                text="Fetch Location"
                onClick={() => handleFetch()}
                disabled={!isAnySelectionMade || !isPopupSelectionValid}
              />
            ) :  (
              <>
                <PrimaryButton
                  text="Fetch Self"
                  onClick={() => handleFetch('SELF')}
                  disabled={!isAnySelectionMade || !isPopupSelectionValid}
                />

                <PrimaryButton
                  text="Fetch Team"
                  onClick={() => handleFetch('TEAM')}
                  disabled={!isAnySelectionMade || !isPopupSelectionValid}
                />
              </>
            )}

            <PrimaryButton
              text="Reset"
              bgColor="#666"
              onClick={handleReset}
            />
          </div>

          {/* <DatePicker reset={resetDates} onFromDateChange={setFromDate} onToDateChange={setToDate} /> */}
        </div>

        {
          popupError && (
          <div className="validation-error-message">
            {popupError}
          </div>
        )}    

        {feedback.message && (
          <Feedback
            message={feedback.message}
            type={feedback.type}
            onClose={handleFeedbackClose}
          />
        )}

        {isPopupVisible && (
          <SearchPopup
            agents={categories}
            onClose={() => {
              if (!agentSelected) {
                setPopupError('Select a role before closing the popup to continue.');
              }
              setIsPopupVisible(false);
              setAgentSelected(false);
              setCategories([]);
            }}
            onAgentSelect={handleAgentSelect}
            feedback={feedback}
            title={popupTitle}
            roleType={roleType}
          />
        )}
      </div>

     {loading && (
        <div className="freeze-overlay">
          <CircularProgress size={60} thickness={4.5} />
        </div>
      )}

    </div>
  );
};

export default SearchMenu;
